import PageErrorFallback from '@app/components/layout/page/components/page-error-fallback';
import { ErrorBoundary } from '@sentry/react';
import { useGetPurchasedFoodAndDrinkItem } from '@shared/api/hooks/purchased-goods/food-and-drink';
import { FoodActivityRetrieve } from '@shared/api/types';
import { useHistory } from '@shared/providers/history-provider';
import { QueryObserverResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

export type TContext = {
  item: FoodActivityRetrieve;
  status: 'error' | 'success' | 'loading';
  foodActivityUuid?: string;
  refetchItem: () => Promise<
    QueryObserverResult<FoodActivityRetrieve, unknown>
  >;
  referrer?: string;
};

const Context = createContext<TContext | null>(null);

export function usePurchasedFoodAndDrinkItem() {
  return useContext(Context) as TContext;
}

export default function PurchasedFoodAndDrinkItemContext() {
  const params = useParams();
  const foodActivityUuid = params.item_uuid;
  const history = useHistory();
  const [referrer] = useState<string | undefined>(history.previousLocation);

  const { data, status, refetch } = useGetPurchasedFoodAndDrinkItem({
    activityUuid: foodActivityUuid!,
  });

  const refetchItem = useCallback(() => {
    return refetch();
  }, [refetch]);

  const context: TContext = {
    item: data!,
    status,
    foodActivityUuid,
    refetchItem,
    referrer,
  };

  return (
    <>
      <ErrorBoundary fallback={<PageErrorFallback />}>
        <Context.Provider value={context}>
          <Outlet />
        </Context.Provider>
      </ErrorBoundary>
    </>
  );
}
